import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { useHistory } from "react-router-dom";

import "./index.scss";

/**
 * Standard page frame: one max-width, one gutter, one title treatment.
 *
 * Pages that render their own padding and heading drift apart as they are
 * edited independently, which is what makes navigating between routes feel
 * uneven. Route new pages through here instead.
 */
export default function PageShell({
  title,
  subtitle,
  actions,
  onBack,
  showBack,
  width,
  disableGutters,
  children,
}) {
  const history = useHistory();
  const hasHeader = Boolean(title || actions || showBack);

  return (
    <Box className={`page-shell page-shell--${width}${disableGutters ? " page-shell--flush" : ""}`}>
      {hasHeader && (
        <Stack className="page-shell__header" direction="row" alignItems="flex-start" spacing={2}>
          {showBack && (
            <IconButton
              className="page-shell__back"
              aria-label="Go back"
              onClick={onBack || (() => history.goBack())}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}

          <Box className="page-shell__titles">
            {title && (
              <Typography component="h1" className="page-shell__title">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography component="p" className="page-shell__subtitle">
                {subtitle}
              </Typography>
            )}
          </Box>

          {actions && <Box className="page-shell__actions">{actions}</Box>}
        </Stack>
      )}

      <Box className="page-shell__body">{children}</Box>
    </Box>
  );
}

PageShell.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  onBack: PropTypes.func,
  showBack: PropTypes.bool,
  width: PropTypes.oneOf(["narrow", "standard", "wide", "full"]),
  disableGutters: PropTypes.bool,
  children: PropTypes.node,
};

PageShell.defaultProps = {
  title: null,
  subtitle: null,
  actions: null,
  onBack: null,
  showBack: false,
  width: "standard",
  disableGutters: false,
  children: null,
};
